import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_TONES = {
  new: { label: "New", color: "#e8b4b8" },
  reviewed: { label: "Reviewed", color: "#9aa0a6" },
  "publish-ready": { label: "Publish-Ready", color: "#d4af37" },
  converted: { label: "Converted → Draft", color: "#8fbf9f" },
  archived: { label: "Archived", color: "#5a5a66" },
};

export function StoriesPanel({ stories, reload }) {
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const counts = stories.reduce((acc, s) => {
    const k = s.status || "new";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  const filtered =
    filter === "all" ? stories : stories.filter((s) => (s.status || "new") === filter);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/stories/${id}`, { status });
      toast.success(`Marked as ${STATUS_TONES[status]?.label || status}`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err) || "Update failed");
    }
  };

  const convertToBlog = async (story, polish = true) => {
    if (!story.publish_consent) {
      toast.error("Submitter did not give publish consent.");
      return;
    }
    const label = polish ? "with AI polish" : "as a raw draft";
    if (!confirm(`Convert "${story.story_title || "(untitled)"}" to a blog draft ${label}?\n\nThe draft will stay UNPUBLISHED until you review and publish from the Articles tab.`)) {
      return;
    }
    try {
      const r = await api.post(
        `/admin/stories/${story.id}/convert-to-blog?polish=${polish ? 1 : 0}`
      );
      const polishNote = r.data.ai_polished ? "AI-polished " : "";
      toast.success(`${polishNote}Draft created — open Articles tab to review (slug: ${r.data.slug})`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err) || "Convert failed");
    }
  };

  const removeStory = async (id) => {
    if (!confirm("Permanently delete this story submission?")) return;
    try {
      await api.delete(`/admin/stories/${id}`);
      toast.success("Story deleted");
      reload();
    } catch (err) {
      toast.error(formatApiError(err) || "Delete failed");
    }
  };

  const filterChips = [
    ["all", `All (${stories.length})`],
    ["new", `New (${counts.new || 0})`],
    ["reviewed", `Reviewed (${counts.reviewed || 0})`],
    ["publish-ready", `Publish-Ready (${counts["publish-ready"] || 0})`],
    ["converted", `Converted (${counts.converted || 0})`],
    ["archived", `Archived (${counts.archived || 0})`],
  ];

  return (
    <div data-testid="stories-panel" className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-white/55">
          {filtered.length} stor{filtered.length === 1 ? "y" : "ies"}
        </p>
        <div className="flex flex-wrap gap-2">
          {filterChips.map(([k, label]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`stories-filter-${k}`}
              className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border transition-all ${
                filter === k
                  ? "border-[color:var(--gold)] text-[color:var(--gold)] bg-[color:var(--gold)]/10"
                  : "border-white/10 text-white/55 hover:text-white/85 hover:border-white/30"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="surface p-12 text-center text-white/40 italic">
          No stories in this view yet.
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((s) => {
          const isAnon = s.anonymous || !s.name;
          const status = s.status || "new";
          const tone = STATUS_TONES[status] || STATUS_TONES.new;
          const isOpen = expanded === s.id;
          return (
            <div
              key={s.id}
              data-testid={`story-row-${s.id}`}
              className="surface p-5 lg:p-6 transition-all"
            >
              <div className="grid lg:grid-cols-12 gap-4 items-start">
                <div className="lg:col-span-3 space-y-1">
                  <p className="font-serif text-lg tracking-tight">
                    {isAnon ? (
                      <em className="italic text-white/65">Anonymous</em>
                    ) : (
                      s.name
                    )}
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">
                    {s.relationship || "—"}
                  </p>
                  {!isAnon && s.email && (
                    <p className="text-xs text-white/45">{s.email}</p>
                  )}
                </div>

                <div className="lg:col-span-5 space-y-1.5">
                  {s.story_title && (
                    <p
                      className="font-serif text-base tracking-tight"
                      style={{ color: "var(--gold)" }}
                    >
                      {s.story_title}
                    </p>
                  )}
                  <p className="text-sm text-white/65 leading-snug line-clamp-2">
                    {s.story}
                  </p>
                </div>

                <div className="lg:col-span-2 space-y-2">
                  <span
                    className="inline-block text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border"
                    style={{
                      color: tone.color,
                      borderColor: `${tone.color}66`,
                      background: `${tone.color}10`,
                    }}
                  >
                    {tone.label}
                  </span>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/35">
                    {s.publish_consent ? "✓ Publish OK" : "Private"}
                  </div>
                  <div className="text-[10px] text-white/35">
                    {new Date(s.created_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="lg:col-span-2 flex flex-col gap-2 items-end">
                  <button
                    onClick={() => setExpanded(isOpen ? null : s.id)}
                    className="text-[10px] uppercase tracking-[0.2em] text-white/55 hover:text-[color:var(--gold)]"
                    data-testid={`story-toggle-${s.id}`}
                  >
                    {isOpen ? "Collapse" : "Read full"}
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="mt-5 pt-5 border-t border-white/5 space-y-5">
                  <div
                    className="font-serif text-base text-white/85 leading-[1.7] whitespace-pre-wrap"
                    data-testid={`story-body-${s.id}`}
                  >
                    {s.story}
                  </div>

                  <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-white/5">
                    {status !== "reviewed" && (
                      <button
                        onClick={() => updateStatus(s.id, "reviewed")}
                        className="btn-ghost-gold text-[10px] uppercase tracking-[0.2em] px-4 py-2"
                        data-testid={`story-mark-reviewed-${s.id}`}
                      >
                        Mark Reviewed
                      </button>
                    )}
                    {status !== "publish-ready" && s.publish_consent && (
                      <button
                        onClick={() => updateStatus(s.id, "publish-ready")}
                        className="btn-ghost-gold text-[10px] uppercase tracking-[0.2em] px-4 py-2"
                        data-testid={`story-flag-publish-${s.id}`}
                      >
                        Flag Publish-Ready
                      </button>
                    )}
                    {s.publish_consent && status !== "converted" && (
                      <>
                        <button
                          onClick={() => convertToBlog(s, true)}
                          className="btn-gold text-[10px] uppercase tracking-[0.2em] px-4 py-2 inline-flex items-center gap-1.5"
                          data-testid={`story-convert-polish-${s.id}`}
                          title="AI lightly edits for grammar/flow + adds a Nina-voiced editor's intro"
                        >
                          ✦ Convert + AI Polish
                        </button>
                        <button
                          onClick={() => convertToBlog(s, false)}
                          className="text-[10px] uppercase tracking-[0.2em] text-white/55 hover:text-[color:var(--gold)] px-3 py-2"
                          data-testid={`story-convert-raw-${s.id}`}
                          title="Convert exactly as submitted, no AI editing"
                        >
                          Convert (raw)
                        </button>
                      </>
                    )}
                    {status !== "archived" && (
                      <button
                        onClick={() => updateStatus(s.id, "archived")}
                        className="text-[10px] uppercase tracking-[0.2em] text-white/45 hover:text-white/85 px-3 py-2"
                        data-testid={`story-archive-${s.id}`}
                      >
                        Archive
                      </button>
                    )}
                    <button
                      onClick={() => removeStory(s.id)}
                      className="text-[10px] uppercase tracking-[0.2em] text-red-400/70 hover:text-red-300 ml-auto px-3 py-2"
                      data-testid={`story-delete-${s.id}`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
